import ApiService from './api.service'


class NewsletterError extends Error {
    
    constructor(errorCode, message) {
        super(message)
        this.name = this.constructor.name
        this.message = message
        this.errorCode = errorCode
    }
}

const NewsletterService = {
    /**
     * Fetch current user's newsletter list and store the access token to StorageService. 
     * 
     * @returns newsletterList
     * @throws NewsletterError 
    **/
    fetchAll: async function(page) {
        try {
            const response = await ApiService.get(`/newsletters/list?page=${page}`)
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Create new newsletter
     * 
     * @returns newsletterList
     * @throws NewsletterError 
    **/
    createNewsletter: async function(data) {
        try {

            const response = await ApiService.post("/newsletters/create", data)
            
            return response.data

        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Fetch newsletter list by id. 
     * 
     * @returns Newsletter
     * @throws NewsletterError 
    **/
    fetchNewsletter: async function(id) {
        try {
            const response = await ApiService.get(`/newsletters/${id}`)
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete newsletter list by id. 
     * 
     * @returns Newsletter
     * @throws NewsletterError 
    **/
    deleteNewsletter: async function(id) {
        try {
            const response = await ApiService.delete(`/newsletters/${id}`)
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Update newsletter
     * 
     * @returns newsletterList
     * @throws NewsletterError 
    **/
    updateNewsletter: async function(data, id) {
        try {
            const response = await ApiService.put(`/newsletters/${id}`, data)
            
            return response.data

        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete multi newsletter lists by ids. 
     * 
     * @returns Newsletter
     * @throws NewsletterError 
    **/
    deleteMultipleNewsletters: async function(ids) {
        try {
            const response = await ApiService.delete("/newsletters/bulk", {'ids':ids})
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Add subscriber to newsletter 
     * 
     * @returns newsletterList
     * @throws NewsletterError 
    **/
    addSubscriber: async function(newsletterId, data) {
        try {
            const response = await ApiService.post(`/newsletters/${newsletterId}/subscribers`, data)
            
            return response.data

        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Fetch newsletter subscriber by id. 
     * 
     * @returns NewsletterSubscriber
     * @throws NewsletterError 
    **/
    fetchSubscriber: async function(newsletterId, id) {
        try {
            const response = await ApiService.get(`/newsletters/${newsletterId}/subscribers/${id}`)
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Add bulk subscriber to newsletter 
     * 
     * @returns newsletterList
     * @throws NewsletterError 
    **/
    addSubscriberBulk: async function(newsletterId, data) {
        try {

            const response = await ApiService.post(`/newsletters/${newsletterId}/subscribers/bulk`, data)

            return response.data

        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }   
    },

    /**
     * Fetch newsletter subscribers
     * 
     * @returns subscribers list
     * @throws NewsletterError 
    **/
    fetchSubscribers: async function(newsletterId, page) {

       try {
            const response = await ApiService.get(`/newsletters/${newsletterId}/subscribers/list?page=${page}`)

            return response.data

        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Update subscriber to newsletter 
     * 
     * @returns newsletterList
     * @throws NewsletterError 
    **/
    updateSubscriber: async function(newsletterId, id, data) {
        try {
            const response = await ApiService.put(`/newsletters/${newsletterId}/subscribers/${id}`, data)
            
            return response.data

        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete subscriber by id. 
     * 
     * @returns Subscriber
     * @throws NewsletterError 
    **/
    deleteSubscriber: async function(newsletterId, id) {
        try {
            const response = await ApiService.delete(`/newsletters/${newsletterId}/subscribers/${id}`)
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete multi subscribers by ids. 
     * 
     * @returns Subscribers
     * @throws NewsletterError 
    **/
    deleteMultipleSubscribers: async function(newsletterId, ids) {
        try {
            const response = await ApiService.delete(`newsletters/${newsletterId}/subscribers/delete`, {'ids':ids})
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Save followup to newsletter 
     * 
     * @returns newsletterList
     * @throws NewsletterError 
    **/
    saveFollowup: async function(newsletterId, data) {
        try {
            const response = await ApiService.post(`/newsletters/${newsletterId}/followups`, data)
            
            return response.data

        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Update followup to newsletter 
     * 
     * @returns newsletterList
     * @throws NewsletterError 
    **/
    updateFollowup: async function(newsletterId, id, data) {
        try {
            const response = await ApiService.put(`/newsletters/${newsletterId}/followups/${id}`, data)
            
            return response.data

        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Fetch newsletter followup by id. 
     * 
     * @returns NewsletterFollowup
     * @throws NewsletterError 
    **/
    fetchFollowup: async function(newsletterId, id) {
        try {
            const response = await ApiService.get(`/newsletters/${newsletterId}/followups/${id}`)
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete followup by id. 
     * 
     * @returns response
     * @throws NewsletterError 
    **/
    deleteFollowup: async function(newsletterId, id) {
        try {
            const response = await ApiService.delete(`/newsletters/${newsletterId}/followups/${id}`)
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Fetch newsletter followups
     * 
     * @returns followup list
     * @throws NewsletterError 
    **/
    fetchFollowups: async function(newsletterId, page) {

       try {
            const response = await ApiService.get(`/newsletters/${newsletterId}/followups/list?page=${page}`)

            return response.data

        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete multi followups by ids. 
     * 
     * @returns Followup
     * @throws NewsletterError 
    **/
    deleteMultipleFollowups: async function(newsletterId, ids) {
        try {
            const response = await ApiService.delete(`newsletters/${newsletterId}/followups/delete`, {'ids':ids})
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Fetch newsletter subscriptions. 
     * 
     * @returns Subscriptions
     * @throws NewsletterError 
    **/
    fetchSubscriptions: async function() {
        try {
            const response = await ApiService.get('newsletters/subscriptions')
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Find newsletter by subcode. 
     * 
     * @returns Newsletter
     * @throws NewsletterError 
    **/
    findBySubcode: async function(subcode) {
        try {
            const response = await ApiService.get(`newsletters/subcodes/${subcode}`)
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Subscribe to newsletter. 
     * 
     * @returns ResponseStatus
     * @throws NewsletterError 
    **/
    subscribe: async function(data) {
        try {
            const response = await ApiService.put('newsletters/subscribe', data)
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Unsubscribe from newsletter. 
     * 
     * @returns ResponseStatus
     * @throws NewsletterError 
    **/
    unsubscribe: async function(data) {
        try {
            const response = await ApiService.delete('newsletters/unsubscribe', data)
            
            return response.data
        } catch (error) {
            throw new NewsletterError(error.response.status, error.response.data.detail)
        }
    },
}

export { NewsletterService, NewsletterError }

